import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { clearSessionConsent } from '@core/utils/analytics/consent';
import { GA } from '@core/utils/analytics/events';
import { identifyUser, track } from '@core/utils/analytics/client';
import {
  completePasswordSetup,
  loginWithPassword,
  refreshWithTokens,
  resendLogin2fa,
  resendPasswordSetup,
  startPasswordSetup,
  verifyLogin2fa,
  verifyPasswordSetup,
  type AuthAckResult,
  type AuthApiResult,
  type AuthChallengeResult,
  type AuthLoginResult,
  type LoginPasswordOptions,
} from './authClient';
import { clearTokens, decodeToken, getRefreshToken, getToken, isExpired, setTokens } from './session';

interface AccountUser {
  sub: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  source?: 'hedgefacts' | 'internal';
  companyKey?: string;
  datasource?: string;
  isAggregator?: boolean;
  role?: 'owner' | 'admin' | 'user';
  fundsEnabled?: boolean;
  managedAccountsEnabled?: boolean;
}

interface AccountContextValue {
  user: AccountUser | null;
  /** Password step — MFA challenge, or session when MFA trust is still valid. */
  login: (email: string, password: string, options: LoginPasswordOptions) => Promise<AuthLoginResult>;
  verify2fa: (challengeToken: string, code: string) => Promise<AuthApiResult>;
  resend2fa: (challengeToken: string) => Promise<AuthChallengeResult>;
  startPasswordSetup: (email: string) => Promise<AuthChallengeResult>;
  resendPasswordSetup: (challengeToken: string) => Promise<AuthChallengeResult>;
  verifyPasswordSetup: (challengeToken: string, code: string) => Promise<AuthAckResult>;
  completePasswordSetup: (challengeToken: string, code: string, newPassword: string) => Promise<AuthApiResult>;
  logout: () => void;
  /** Session expiry / 401 — keeps MFA trust so the next password login can skip email 2FA. */
  expireSession: () => void;
  applyTokens: (accessToken: string, refreshToken: string, mfaTrustToken?: string) => void;
  getJwtToken: () => string | undefined;
  refreshIfNeeded: () => Promise<string | undefined>;
}

const AccountContext = createContext<AccountContextValue | undefined>(undefined);

function userFromToken(token: string | undefined): AccountUser | null {
  if (!token) return null;
  try {
    const claims = decodeToken(token);
    if (typeof claims.sub !== 'string') return null;
    return {
      sub: claims.sub,
      ...(claims.email ? { email: claims.email } : {}),
      ...(claims.firstName ? { firstName: claims.firstName } : {}),
      ...(claims.lastName ? { lastName: claims.lastName } : {}),
      ...(claims.source ? { source: claims.source } : {}),
      ...(claims.companyKey ? { companyKey: claims.companyKey } : {}),
      ...(claims.datasource ? { datasource: claims.datasource } : {}),
      ...(claims.isAggregator === true ? { isAggregator: true } : {}),
      ...(claims.role ? { role: claims.role } : {}),
      ...(typeof claims.fundsEnabled === 'boolean' ? { fundsEnabled: claims.fundsEnabled } : {}),
      ...(typeof claims.managedAccountsEnabled === 'boolean' ? { managedAccountsEnabled: claims.managedAccountsEnabled } : {}),
    };
  } catch {
    return null;
  }
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AccountUser | null>(() => userFromToken(getToken()));
  const sessionGen = useRef(0);

  const expireSession = useCallback(() => {
    sessionGen.current += 1;
    track(GA.sessionExpired);
    clearTokens();
    clearSessionConsent();
    setUser(null);
  }, []);

  const logout = useCallback(() => {
    sessionGen.current += 1;
    track(GA.signedOut);
    clearTokens({ revokeMfaTrust: true });
    clearSessionConsent();
    setUser(null);
  }, []);

  const applySession = useCallback((token: string, refreshToken: string, mfaTrustToken?: string) => {
    setTokens(token, refreshToken, mfaTrustToken);
    const next = userFromToken(token);
    if (next) identifyUser(next);
    setUser((prev) => {
      if (
        prev?.sub === next?.sub &&
        prev?.source === next?.source &&
        prev?.companyKey === next?.companyKey &&
        prev?.datasource === next?.datasource &&
        prev?.isAggregator === next?.isAggregator &&
        prev?.role === next?.role &&
        prev?.email === next?.email &&
        prev?.firstName === next?.firstName &&
        prev?.lastName === next?.lastName &&
        prev?.fundsEnabled === next?.fundsEnabled &&
        prev?.managedAccountsEnabled === next?.managedAccountsEnabled
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string, options: LoginPasswordOptions): Promise<AuthLoginResult> => {
      const gen = sessionGen.current;
      const result = await loginWithPassword(email, password, options);
      if (gen !== sessionGen.current) return result;
      if (result.ok === false) {
        if (!result.aborted) track(GA.signInFailed);
        return result;
      }
      if (!('tokens' in result)) {
        track(GA.signInNeedsCode);
        return result;
      }
      applySession(result.tokens.accessToken, result.tokens.refreshToken, result.tokens.mfaTrustToken);
      track(GA.signedIn);
      return result;
    },
    [applySession],
  );

  const verify2fa = useCallback(
    async (challengeToken: string, code: string): Promise<AuthApiResult> => {
      const gen = sessionGen.current;
      const result = await verifyLogin2fa(challengeToken, code);
      if (gen !== sessionGen.current) return result;
      if (result.ok === false) {
        if (!result.aborted) track(GA.signInFailed);
        return result;
      }
      applySession(result.tokens.accessToken, result.tokens.refreshToken, result.tokens.mfaTrustToken);
      track(GA.verifiedSignInCode);
      return result;
    },
    [applySession],
  );

  const resend2fa = useCallback(async (challengeToken: string): Promise<AuthChallengeResult> => {
    const result = await resendLogin2fa(challengeToken);
    if (result.ok) track(GA.resentSignInCode);
    return result;
  }, []);

  const startPasswordSetupAccount = useCallback(async (email: string): Promise<AuthChallengeResult> => {
    const result = await startPasswordSetup(email);
    if (result.ok) track(GA.startedPasswordSetup);
    return result;
  }, []);

  const resendPasswordSetupAccount = useCallback(async (challengeToken: string): Promise<AuthChallengeResult> => {
    const result = await resendPasswordSetup(challengeToken);
    if (result.ok) track(GA.resentPasswordSetup);
    return result;
  }, []);

  const verifyPasswordSetupAccount = useCallback(async (challengeToken: string, code: string): Promise<AuthAckResult> => {
    const result = await verifyPasswordSetup(challengeToken, code);
    if (result.ok) track(GA.verifiedPasswordSetup);
    return result;
  }, []);

  const completePasswordSetupAccount = useCallback(
    async (challengeToken: string, code: string, newPassword: string): Promise<AuthApiResult> => {
      const gen = sessionGen.current;
      const result = await completePasswordSetup(challengeToken, code, newPassword);
      if (gen !== sessionGen.current) return result;
      if (result.ok === false) return result;
      applySession(result.tokens.accessToken, result.tokens.refreshToken, result.tokens.mfaTrustToken);
      track(GA.finishedPasswordSetup);
      return result;
    },
    [applySession],
  );

  const getJwtToken = useCallback(() => getToken(), []);

  const refreshIfNeeded = useCallback(async (): Promise<string | undefined> => {
    const token = getToken();
    if (token && !isExpired(token)) return token;

    const refreshToken = getRefreshToken();
    // Refresh requires the expired access JWT in X-Expired-Token — cannot mint without it.
    if (!token || !refreshToken || isExpired(refreshToken)) {
      expireSession();
      return undefined;
    }

    const gen = sessionGen.current;
    const result = await refreshWithTokens(refreshToken, token);
    if (gen !== sessionGen.current) return undefined;
    if (!result.ok) {
      expireSession();
      return undefined;
    }

    applySession(result.tokens.accessToken, result.tokens.refreshToken, result.tokens.mfaTrustToken);
    return result.tokens.accessToken;
  }, [expireSession, applySession]);

  const value = useMemo<AccountContextValue>(
    () => ({
      user,
      login,
      verify2fa,
      resend2fa,
      startPasswordSetup: startPasswordSetupAccount,
      resendPasswordSetup: resendPasswordSetupAccount,
      verifyPasswordSetup: verifyPasswordSetupAccount,
      completePasswordSetup: completePasswordSetupAccount,
      logout,
      expireSession,
      applyTokens: applySession,
      getJwtToken,
      refreshIfNeeded,
    }),
    [
      user,
      login,
      verify2fa,
      resend2fa,
      startPasswordSetupAccount,
      resendPasswordSetupAccount,
      verifyPasswordSetupAccount,
      completePasswordSetupAccount,
      logout,
      expireSession,
      applySession,
      getJwtToken,
      refreshIfNeeded,
    ],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used within an AccountProvider');
  return ctx;
}
